import { Paragraph } from '@zillow/constellation-10';
import type { Offense } from '@zg-rentals/ts-capps-client';
import { InlineDd, InlineDt } from '../../BackgroundReportEvictions/EvictionSectionContent';
import { getTitleCase } from '../../../../utils/formatters';
import { getCurrencyDisplay } from '@zg-rentals/applications-utils';
import { formatDate } from '@zg-rentals/formatters';

export function CriminalOffense({ offense }: { offense: Offense }) {
  const {
    caseType,
    description,
    offenseDate,
    offenseCode,
    sentenceLength,
    statute,
    suspendedSentence,
    filingDate,
    disposition,
    dispositionDate,
    convictionDate,
    convictionLocation,
    ncicCode,
    ncicCodeDescription,
    probationSentenceLength,
    amendedDisposition,
    courtName,
    plea,
    courtCosts,
    fines,
    warrantDate,
    warrantCounty,
    supervisionCounty,
    supervisionState,
    supervisionDate,
    commitmentLocation,
    commitmentCounty,
    commitmentState,
    commitmentDate,
    releaseDate,
    fbiNumber,
    bookingNumber,
    restitution,
    status,
  } = offense;
  return (
    <>
      <Paragraph textStyle="body-xs-bold" css={{ color: 'text.subtle', marginTop: 'default', marginBottom: 'tighter' }}>
        Offense details
      </Paragraph>
      {caseType && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Type:</InlineDt> <InlineDd>{getTitleCase(caseType)}</InlineDd>
        </Paragraph>
      )}
      {description && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Description:</InlineDt> <InlineDd>{getTitleCase(description)}</InlineDd>
        </Paragraph>
      )}
      {offenseDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Offense date:</InlineDt> <InlineDd>{formatDate(offenseDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {offenseCode && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Offense code:</InlineDt> <InlineDd>{offenseCode}</InlineDd>
        </Paragraph>
      )}
      {sentenceLength && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Sentence length:</InlineDt> <InlineDd>{sentenceLength.toLowerCase()}</InlineDd>
        </Paragraph>
      )}
      {statute && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Statute:</InlineDt> <InlineDd>{statute}</InlineDd>
        </Paragraph>
      )}
      {suspendedSentence && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Suspended sentence:</InlineDt> <InlineDd>{suspendedSentence.toLowerCase()}</InlineDd>
        </Paragraph>
      )}
      {filingDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Charge filing date:</InlineDt> <InlineDd>{formatDate(filingDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {disposition && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Disposition:</InlineDt> <InlineDd>{disposition}</InlineDd>
        </Paragraph>
      )}
      {dispositionDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Disposition date:</InlineDt> <InlineDd>{formatDate(dispositionDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {convictionDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Conviction date:</InlineDt> <InlineDd>{formatDate(convictionDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {convictionLocation && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Conviction location:</InlineDt> <InlineDd>{convictionLocation}</InlineDd>
        </Paragraph>
      )}
      {ncicCode && (
        <Paragraph textStyle="body-sm">
          <InlineDt>NCIC code:</InlineDt> <InlineDd>{ncicCode}</InlineDd>
        </Paragraph>
      )}
      {ncicCodeDescription && (
        <Paragraph textStyle="body-sm">
          <InlineDt>NCIC code description:</InlineDt> <InlineDd>{ncicCodeDescription}</InlineDd>
        </Paragraph>
      )}
      {probationSentenceLength && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Probation sentence length:</InlineDt> <InlineDd>{probationSentenceLength.toLowerCase()}</InlineDd>
        </Paragraph>
      )}
      {amendedDisposition && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Amended disposition:</InlineDt> <InlineDd>{amendedDisposition}</InlineDd>
        </Paragraph>
      )}
      {courtName && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Court:</InlineDt> <InlineDd>{courtName}</InlineDd>
        </Paragraph>
      )}
      {plea && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Plea:</InlineDt> <InlineDd>{getTitleCase(plea)}</InlineDd>
        </Paragraph>
      )}
      {courtCosts && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Court costs:</InlineDt> <InlineDd>{getCurrencyDisplay(courtCosts)}</InlineDd>
        </Paragraph>
      )}
      {fines && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Fines:</InlineDt> <InlineDd>{getCurrencyDisplay(fines)}</InlineDd>
        </Paragraph>
      )}
      {warrantDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Warrant date:</InlineDt> <InlineDd>{formatDate(warrantDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {warrantCounty && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Warrant county:</InlineDt> <InlineDd>{getTitleCase(warrantCounty)}</InlineDd>
        </Paragraph>
      )}
      {supervisionCounty && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Supervision county:</InlineDt> <InlineDd>{getTitleCase(supervisionCounty)}</InlineDd>
        </Paragraph>
      )}
      {supervisionState && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Supervision state:</InlineDt> <InlineDd>{supervisionState}</InlineDd>
        </Paragraph>
      )}
      {supervisionDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Supervision date:</InlineDt> <InlineDd>{formatDate(supervisionDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {commitmentLocation && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Commitment location:</InlineDt> <InlineDd>{commitmentLocation}</InlineDd>
        </Paragraph>
      )}
      {commitmentCounty && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Commitment county:</InlineDt> <InlineDd>{getTitleCase(commitmentCounty)}</InlineDd>
        </Paragraph>
      )}
      {commitmentState && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Commitment state:</InlineDt> <InlineDd>{commitmentState}</InlineDd>
        </Paragraph>
      )}
      {commitmentDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Commitment date:</InlineDt> <InlineDd>{formatDate(commitmentDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {releaseDate && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Release date:</InlineDt> <InlineDd>{formatDate(releaseDate, 'numbers')}</InlineDd>
        </Paragraph>
      )}
      {fbiNumber && (
        <Paragraph textStyle="body-sm">
          <InlineDt>FBI number:</InlineDt> <InlineDd>{fbiNumber}</InlineDd>
        </Paragraph>
      )}
      {bookingNumber && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Booking number:</InlineDt> <InlineDd>{bookingNumber}</InlineDd>
        </Paragraph>
      )}
      {restitution && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Restitution:</InlineDt> <InlineDd>{getCurrencyDisplay(restitution)}</InlineDd>
        </Paragraph>
      )}
      {status && (
        <Paragraph textStyle="body-sm">
          <InlineDt>Status:</InlineDt>{' '}
          <InlineDd>{status.toLowerCase() === 'blank' ? null : getTitleCase(status)}</InlineDd>
        </Paragraph>
      )}
    </>
  );
}
